import type { IWeekday } from "@/api/Activities";
import {
  batchTemplateActivityDelayedStartDateOptionsSchema,
  batchTemplateActivityDelayedStartDateUnitSchema,
} from "@/pages/template-manager/BatchTemplateForm/batchTemplateFormSchema";
import { addDays, addWeeks, nextDay } from "date-fns";
import {
  calendarDateSchema,
  machineFormat,
} from "@medoma/models/date-and-time";
import type { IBatchTemplateActivity } from "./newActivityFromBatchTemplateActivity";

/**
 * Resolves the actual start date for a batch template activity.
 *
 * Inputs:
 * - `activity` — the template activity, whose `delayedStartDate` chooses the rule
 * - `batchStartDate` — the start date the consumer picked for the whole batch
 * - `individualActivityPickedStartDate` — only used when the template says `delayedStartDate: "userSelects"`;
 *   the date the consumer picked for this specific activity at consumption time
 *
 * Rules:
 * - `none`        → use `batchStartDate` as-is
 * - `offset`      → `batchStartDate` shifted by N days/weeks
 * - `weekday`     → next occurrence of the chosen weekday after `batchStartDate`
 * - `userSelects` → use `individualActivityPickedStartDate`
 */
export const resolveActivityStartDate = (
  activity: Pick<
    IBatchTemplateActivity,
    | "delayedStartDate"
    | "delayedStartDateOffset"
    | "delayedStartDateUnit"
    | "delayedStartDateWeekday"
  >,
  batchStartDate: string,
): string => {
  const { delayedStartDate } = activity;

  switch (delayedStartDate) {
    case batchTemplateActivityDelayedStartDateOptionsSchema.enum.none:
      return batchStartDate;

    case batchTemplateActivityDelayedStartDateOptionsSchema.enum.offset: {
      const offset = activity.delayedStartDateOffset;
      const unit = activity.delayedStartDateUnit;

      // Form validation in @/pages/template-manager/BatchTemplateForm/BatchTemplateActivityForm/BatchTemplateActivityStartDateDelayPicker.tsx
      // should ensure that this is not null, but we'll add a check here just in case.
      if (!offset || !unit) {
        throw new Error("Delayed start date offset and unit are required");
      }

      const offsetNumber = parseInt(offset, 10);
      const batchStartDateAsDate = calendarDateSchema.decode(batchStartDate);

      if (unit === batchTemplateActivityDelayedStartDateUnitSchema.enum.week) {
        return machineFormat(
          addWeeks(batchStartDateAsDate, offsetNumber),
          "yyyy-MM-dd",
        );
      }

      return machineFormat(
        addDays(batchStartDateAsDate, offsetNumber),
        "yyyy-MM-dd",
      );
    }

    case batchTemplateActivityDelayedStartDateOptionsSchema.enum.weekday: {
      const weekday = activity.delayedStartDateWeekday;

      // Form validation in @/pages/template-manager/BatchTemplateForm/BatchTemplateActivityForm/BatchTemplateActivityStartDateDelayPicker.tsx
      // should ensure that this is not null, but we'll add a check here just in case.
      if (!weekday) {
        throw new Error("Delayed start date weekday is required");
      }

      // Map weekday string to day index (0 = Sunday, 1 = Monday, etc.)
      const weekdayMap: Record<IWeekday, 0 | 1 | 2 | 3 | 4 | 5 | 6> = {
        Sunday: 0,
        Monday: 1,
        Tuesday: 2,
        Wednesday: 3,
        Thursday: 4,
        Friday: 5,
        Saturday: 6,
      };

      const dayIndex = weekdayMap[weekday];

      return machineFormat(
        nextDay(calendarDateSchema.decode(batchStartDate), dayIndex),
        "yyyy-MM-dd",
      );
    }

    case batchTemplateActivityDelayedStartDateOptionsSchema.enum.userSelects: {
      // Server will respond with "date in wrong format" - user sees default error message.
      // Will be cleaned up when removing userSelects from the model
      return "";
    }

    default:
      throw delayedStartDate satisfies never;
  }
};
